import { getPanelTop } from 'weblite-router'
import {
  getRoom,
  getRoomPermissions,
  isClientsRoom,
  RoomPermission,
  storageByRoomType,
} from '$logic/room'
import { setSubscriptionUnreadMark } from '$logic/subscription'
import { getPv, setPvUnreadMark } from '$logic/pv'
import { fetchUserStatus } from '$logic/users'
import { openRoomPage } from '$logic/router'
import { setPrivateSpaceUnreadMark } from '$logic/private-space'
import { setWappRoomUnreadMark } from '$logic/wapp-room'
import { openedRoomMessagesStorage } from '$logic/opened-room/opened-room-messages.storage'
import { initiateRoomPinMessages, removeRoomPinsSync } from '$logic/pin'
import { checkProfileOnOpenRoom } from '$logic/profile'
import {
  fetchMessagesFromLastOpenedRoomMessageStorage,
  initiateRoomMessage,
} from './opened-room-messages.service'
import { lastOpenedRoomMessagesService } from '$logic/last-opened-room-messages/service'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { isDesktopSize } from '$helper/functions/environmentInfo'

export async function onOpenRoom(roomId: string, openingIid?: number) {
  removeRoomPinsSync()

  const shouldUsePreloadedMessages = openingIid
    ? false
    : fetchMessagesFromLastOpenedRoomMessageStorage(roomId)

  //  NOTE: load Messages
  await initiateRoomMessage(openingIid, shouldUsePreloadedMessages)
  initiateRoomPinMessages(roomId)

  const room = await getRoom(roomId)
  if (!room) return

  if (room.type === 'PV') {
    //  NOTE: If room is PV, send request for user status
    const pv = await getPv(roomId)
    fetchUserStatus(pv?.to)
    setPvUnreadMark(roomId, false)
  } else if (room.type === 'WAPP_ROOM') setWappRoomUnreadMark(roomId, false)
  else if (room.type === 'PRIVATE_SPACE') setPrivateSpaceUnreadMark(false)
  else if (['GROUP', 'CHANNEL'].includes(room.type)) {
    // ignore if public hall
    if (isClientsRoom(roomId)) setSubscriptionUnreadMark(roomId, false)
  }
  checkProfileOnOpenRoom(roomId)
}

export async function openRoom(roomId: string, openingIid?: number) {
  if (!(await canSwitchRoom())) return

  const openedRoomId = getOpenedRoomId()

  const room = await getRoom(roomId)
  if (!room || (roomId === openedRoomId && !openingIid)) return

  if (!(openingIid && roomId === openedRoomId) && isDesktopSize())
    // some times we reopen room with openingIid so we don't need to sync because just room scrolls
    lastOpenedRoomMessagesService.syncOpenedRoomWithLastOpenedRoomMessages()

  openedRoomMessagesStorage.deleteAll()

  openRoomPage(roomId)
  await onOpenRoom(roomId, openingIid)
}

async function canSwitchRoom() {
  const isRecording = await sendFieldApis.checkVoiceStatus()
  if (isRecording) return false
  return true
}

export function getOpenedRoomId() {
  return getPanelTop('room')?.state?.roomId || null
}

export function getOpenedRoom() {
  const roomId = getOpenedRoomId()
  if (roomId) return getRoom(roomId)
  return null
}

export async function getOpenedRoomData() {
  const room = await getOpenedRoom()
  if (!room) return null
  const { get } = storageByRoomType(room.type)
  return get(room.id)
}

export async function openedRoomPermissions(): Promise<RoomPermission> {
  const roomId = getOpenedRoomId()
  return getRoomPermissions(roomId)
}
